import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth, api, JOB_CATEGORIES, JOB_DURATIONS, COUNTRIES } from '@/lib/context';
import { usePrefs } from '@/lib/usePrefs';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import { TranslatableText } from '@/components/TranslatableText';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Briefcase, MapPin, Clock, Globe, Building2, 
  Share2, Loader2, ChevronRight, Calendar, Users,
  Upload, CheckCircle2, Mail, FileText, ArrowRight, User, Shield, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { LockedContactCard } from '@/components/LockedContactCard';
import { ShareButton, ShareButtonsInline } from '@/components/ShareButton';
import { SEOHead } from '@/components/SEOHead';
import { BrowseByCountryCard } from '@/components/SEONavLinks';

// Navigation Component
const NavBar = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.jobs')}
            </Link>
            <Link to="/tools" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.tools')}
            </Link>
            <Link to="/auto-repair" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.autoRepair')}
            </Link>
            <Link to="/services" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.services')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to={user.role === 'company' ? '/company/dashboard' : user.role === 'admin' ? '/admin' : '/worker/dashboard'}>
                  <Button variant="outline" size="sm">{t('nav.dashboard')}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout}>{t('auth.logout')}</Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('auth.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('auth.register')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Job Card Component with dynamic translation
const JobCard = ({ job }) => {
  const { t } = useLanguage();
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(job.title);
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(job.description);
  const isContentTranslated = titleTranslated || descTranslated;
  
  return (
    <Card className="card-hover" data-testid={`job-card-${job.id}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex gap-3">
            {/* Company Logo */}
            {job.company_logo ? (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${job.company_logo}`}
                alt={job.company_name}
                className="w-10 h-10 rounded-lg object-cover border flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <CardTitle className="text-lg">
                {translatedTitle}
                {isContentTranslated && (
                  <Languages className="inline-block ml-2 h-3.5 w-3.5 text-blue-500" title={t('common.translated')} />
                )}
              </CardTitle>
              <CardDescription className="flex items-center gap-1 mt-1">
                {job.company_name}
              </CardDescription>
            </div>
          </div>
          <ShareButton 
            entityType="job" 
            entityId={job.id} 
            title={job.title}
            description={job.description}
            location={`${job.region}, ${job.country}`}
            companyName={job.company_name}
            category={job.category}
            className="h-8 w-8 p-0"
            iconOnly
          />
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {translatedDesc}
        </p>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <TranslatableText text={job.region} />, {t(`location.countries.${job.country}`)}
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t(`durations.${job.duration}`)}
          </Badge>
          {job.language_requirement && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Globe className="h-3 w-3" />
              {job.language_requirement}
            </Badge>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t('jobs.applicationsCount', { count: job.application_count || 0 })}
          </span>
          <ShareButtonsInline 
            entityType="job" 
            entityId={job.id} 
            title={job.title}
          />
        </div>
        <Link to={`/jobs/${job.id}`}>
          <Button size="sm" data-testid={`view-job-${job.id}`}>
            {t('common.viewDetails')}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

// Jobs List Page
export const JobsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { prefs } = usePrefs();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    country: prefs.workCountry || 'all',
    region: prefs.locationQuery || '',
    category: 'all',
    duration: 'all',
    language: ''
  });

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      const res = await api.getJobs(params);
      // Defensive: ensure jobs is always an array
      const data = res?.data;
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError(t('jobs.unableToLoad'));
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t('jobs.title')}</h1>
            <p className="text-muted-foreground">{t('jobs.subtitle')}</p>
          </div>
          {user?.role === 'company' && (
            <Link to="/jobs/create">
              <Button data-testid="create-job-btn">
                <Briefcase className="mr-2 h-4 w-4" />
                {t('jobs.createJob')}
              </Button>
            </Link>
          )}
        </div>

        {/* Trust indicators for non-logged in users */}
        {!user && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6" data-testid="trust-banner">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="font-medium text-foreground">{t('microcopy.megaMicrocopy.jobs.browseHeader')}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">{t('microcopy.megaMicrocopy.jobs.browseHelper')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>{t('microcopy.jobs.yourDataYours')}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {t('microcopy.jobs.noRegistrationBrowse')}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {t('microcopy.jobs.applyWithoutAccount')}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {t('microcopy.jobs.detailsProtected')}
              </span>
            </div>
            <p className="text-xs text-brand mt-2 font-medium">{t('microcopy.megaMicrocopy.jobs.browseMotivation')}</p>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-8" data-testid="filters-card">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v})}>
                <SelectTrigger data-testid="filter-country">
                  <SelectValue placeholder={t('jobs.country')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder={t('jobs.region')}
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
                data-testid="filter-region"
              />

              <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder={t('jobs.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {JOB_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.duration} onValueChange={(v) => setFilters({...filters, duration: v})}>
                <SelectTrigger data-testid="filter-duration">
                  <SelectValue placeholder={t('jobs.duration')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {JOB_DURATIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{t(d.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={() => setFilters({country: 'all', region: '', category: 'all', duration: 'all', language: ''})}>
                {t('common.clearFilters')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Jobs Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-loading">
            {[1,2,3,4,5,6].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="text-center py-12" data-testid="jobs-error">
            <CardContent>
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-xl font-semibold mb-2">{t('common.somethingWentWrong')}</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={fetchJobs}>
                {t('common.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        ) : !jobs || jobs.length === 0 ? (
          <Card className="text-center py-12" data-testid="jobs-empty">
            <CardContent>
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-xl font-semibold mb-2">{t('jobs.noJobsFound')}</p>
              <p className="text-muted-foreground">{t('jobs.beFirstToApply')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}
        
        {/* SEO Navigation - Browse by Country/Category */}
        <div className="mt-12">
          <BrowseByCountryCard 
            basePath="/jobs" 
            showCategories={true}
          />
        </div>
      </div>
    </div>
  );
};

// Job Detail Page
export const JobDetailPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const jobId = window.location.pathname.split('/').pop();
  const [job, setJob] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  
  // Guest application state
  const [guestEmail, setGuestEmail] = useState('');
  const [guestCvFile, setGuestCvFile] = useState(null);
  const [guestCoverLetter, setGuestCoverLetter] = useState('');
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const fileInputRef = useRef(null);
  
  // Dynamic content translation - call hooks unconditionally with fallback values
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(job?.title || '');
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(job?.description || '');
  const isContentTranslated = titleTranslated || descTranslated;

  useEffect(() => {
    fetchJob();
    fetchSeoData();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const res = await api.getJob(jobId);
      setJob(res.data);
    } catch (error) {
      toast.error(t('common.error'));
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoData = async () => {
    try {
      // Fetch public SEO-enhanced data
      const res = await api.get(`/public/jobs/${jobId}`);
      setSeoData(res.data);
    } catch (error) {
      // SEO data is optional, don't fail if not available
      console.log('SEO data not available:', error);
    }
  };

  // Authenticated user application
  const handleApply = async () => {
    if (!user) {
      toast.error(t('jobs.loginToApply'));
      navigate('/login');
      return;
    }
    if (user.role !== 'worker') {
      toast.error(t('common.error'));
      return;
    }

    setApplying(true);
    try {
      await api.applyToJob(jobId, { job_id: jobId, cover_letter: coverLetter });
      toast.success(t('jobs.applicationSubmitted'));
      setApplyDialogOpen(false);
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setApplying(false);
    }
  };

  // Guest application handler
  const handleGuestApply = async () => {
    if (applying) return; // Idempotency guard against double-clicks

    if (!guestEmail || !guestCvFile) {
      toast.error('Please provide email and CV file');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setApplying(true);
    try {
      const formData = new FormData();
      formData.append('job_id', jobId);
      formData.append('email', guestEmail);
      formData.append('cv_file', guestCvFile);
      if (guestCoverLetter) {
        formData.append('cover_letter', guestCoverLetter);
      }

      const res = await api.guestApply(formData);
      // Only mark success once the server confirms the DB insert AND
      // returns a valid application id. Do NOT show the success page
      // otherwise.
      if (!res?.data?.id) {
        throw new Error('server_no_id');
      }
      setApplicationId(res.data.id);
      setApplicationSuccess(true);
      toast.success(t('jobs.guestApply.success'));
      fetchJob();
    } catch (error) {
      // Preserve form data (guestEmail, guestCvFile, guestCoverLetter
      // remain in state) so the candidate doesn't lose their input.
      const detail = error?.response?.data?.detail || error?.message;
      const status = error?.response?.status;
      if (status === 429) {
        toast.error(t('jobs.guestApply.rateLimited') || 'Too many attempts — please try again shortly.');
      } else if (status === 400 && String(detail).toLowerCase().includes('already applied')) {
        toast.error(t('jobs.guestApply.alreadyApplied') || 'You have already applied to this job.');
      } else if (status === 404) {
        toast.error(t('jobs.guestApply.jobUnavailable') || 'This job is no longer accepting applications.');
      } else {
        toast.error(detail || t('common.error') || 'Something went wrong. Please try again.');
      }
    } finally {
      setApplying(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Max 10MB allowed.');
        return;
      }
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.txt', '.rtf'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error('Invalid file type. Allowed: PDF, DOC, DOCX, TXT, RTF');
        return;
      }
      setGuestCvFile(file);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!job) return null;

  // Success state after guest application
  if (applicationSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="text-center" data-testid="application-success-card">
            <CardHeader>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl">{t('microcopy.megaMicrocopy.apply.successTitle')}</CardTitle>
              <CardDescription className="text-base mt-2">
                {t('microcopy.megaMicrocopy.apply.successBody')}
              </CardDescription>
              <p className="text-sm text-brand mt-2 font-medium">{t('microcopy.megaMicrocopy.apply.successMotivation')}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-4 text-left">
                <p className="text-sm text-muted-foreground mb-1">{t('jobs.postedBy').replace('{company}', '')}:</p>
                <p className="font-semibold">{job.title}</p>
                <p className="text-sm text-muted-foreground">{job.company_name}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>{t('microcopy.guestApply.employerNotified')}</span>
                </div>
              </div>
              
              {/* What happens next */}
              <div className="border rounded-lg p-4 text-left space-y-3">
                <h3 className="font-semibold">{t('microcopy.guestApply.whatHappensNext')}</h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-brand">1</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{t('microcopy.guestApply.employerReviews')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-brand">2</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{t('microcopy.guestApply.theyReachOut')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-brand">3</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{t('microcopy.guestApply.youDecide')}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{t('microcopy.guestApply.checkYourEmail')}</p>
              </div>
              
              <div className="border rounded-lg p-6 bg-gradient-to-br from-brand/5 to-brand/10">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <User className="h-5 w-5 text-brand" />
                  {t('microcopy.guestApply.stayInLoop')}
                </h3>
                <ul className="text-sm text-muted-foreground mb-4 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    {t('microcopy.guestApply.trackProgress')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    {t('microcopy.guestApply.getNotified')}
                  </li>
                </ul>
                <Link to={`/register?email=${encodeURIComponent(guestEmail)}&application=${applicationId}`}>
                  <Button className="w-full" data-testid="create-account-btn">
                    {t('jobs.guestApply.createAccount')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  {t('microcopy.guestApply.freeForever')}
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Link to="/jobs">
                <Button variant="ghost" data-testid="browse-more-jobs-btn">
                  {t('jobs.guestApply.orContinueBrowsing')}
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Head - Schema.org and Meta Tags */}
      {seoData && (
        <SEOHead
          title={seoData.meta?.title || job?.title}
          description={seoData.meta?.description || job?.description?.substring(0, 160)}
          url={seoData.share_url || window.location.href}
          image={seoData.meta?.openGraph?.image}
          schema={seoData.schema}
          type="article"
        />
      )}
      
      <NavBar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card data-testid="job-detail-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex gap-4">
                {/* Company Logo */}
                {job.company_logo ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${job.company_logo}`}
                    alt={job.company_name}
                    className="w-16 h-16 rounded-xl object-cover border flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <CardTitle className="text-2xl mb-2">
                    {translatedTitle}
                    {isContentTranslated && (
                      <Languages className="inline-block ml-2 h-4 w-4 text-blue-500" title={t('common.translated')} />
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {job.company_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      <TranslatableText text={job.region} />, {t(`location.countries.${job.country}`)}
                    </span>
                  </div>
                </div>
              </div>
              <ShareButton 
                entityType="job" 
                entityId={job.id} 
                title={job.title}
                iconOnly
              />
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge className="capitalize">{t(`categories.${job.category}`)}</Badge>
              <Badge variant="secondary">
                {t(`durations.${job.duration}`)}
              </Badge>
              {job.language_requirement && (
                <Badge variant="outline">
                  <Globe className="h-3 w-3 mr-1" />
                  {job.language_requirement}
                </Badge>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-2">{t('jobs.description')}</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{translatedDesc}</p>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {t('jobs.applicationsCount', { count: job.application_count || 0 })}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {t('jobs.postedBy')} {new Date(job.created_at).toLocaleDateString()}
              </span>
            </div>

            {/* Locked Contact Card - Company contact info */}
            {job.company_user_id && (
              <div className="pt-4 border-t">
                <h3 className="font-semibold mb-3">{t('jobs.companyContact')}</h3>
                <LockedContactCard 
                  targetUserId={job.company_user_id}
                  targetUserName={job.company_name}
                  targetUserRole="company"
                  jobId={job.id}
                  companyName={job.company_name}
                />
              </div>
            )}
            
            {/* AI-Generated SEO Content for Google */}
            {seoData?.seo?.long_content && (
              <div className="pt-4 border-t">
                <h3 className="font-semibold mb-3">{t('jobs.aboutThisJob')}</h3>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {seoData.seo.long_content}
                </div>
              </div>
            )}
            
            {/* Share Button */}
            <div className="pt-4 border-t flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('share.shareJob')}</span>
              <ShareButton 
                entityType="job" 
                entityId={job.id} 
                title={job.title}
                description={job.description}
                location={`${job.region}, ${job.country}`}
                companyName={job.company_name}
                category={job.category}
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            {/* Logged in worker - regular apply */}
            {user?.role === 'worker' && (
              <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full" data-testid="apply-job-btn">
                    {t('jobs.applyNow')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('jobs.applyNow')} - {job.title}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>{t('jobs.coverLetter')}</Label>
                      <Textarea
                        placeholder={t('jobs.coverLetterPlaceholder')}
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        rows={6}
                        data-testid="cover-letter-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('jobs.contactHiddenNote')}
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setApplyDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleApply} disabled={applying} data-testid="submit-application-btn">
                      {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('jobs.submitApplication')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Company user - cannot apply */}
            {user?.role === 'company' && (
              <p className="text-center text-muted-foreground">
                Companies cannot apply to jobs
              </p>
            )}
            
            {/* Not logged in - Guest Application Form */}
            {!user && (
              <div className="w-full space-y-4 border-t pt-4">
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-lg">{t('microcopy.megaMicrocopy.apply.guestTitle')}</h3>
                  <p className="text-sm text-muted-foreground">{t('microcopy.megaMicrocopy.apply.guestSubtitle')}</p>
                  <p className="text-xs text-brand mt-1">{t('microcopy.megaMicrocopy.apply.guestMotivation')}</p>
                  <div className="flex items-center justify-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      {t('microcopy.megaMicrocopy.apply.guestTrust')}
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      {t('microcopy.guestApply.confidential')}
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      {t('microcopy.guestApply.under30Seconds')}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Email Input */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {t('jobs.guestApply.emailLabel')} *
                    </Label>
                    <Input
                      type="email"
                      placeholder={t('jobs.guestApply.emailPlaceholder')}
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      data-testid="guest-email-input"
                    />
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-muted-foreground">{t('jobs.guestApply.emailHelper')}</p>
                      <span className="text-xs text-green-600">• {t('microcopy.guestApply.neverSharedWithout')}</span>
                    </div>
                  </div>
                  
                  {/* CV File Upload */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {t('jobs.guestApply.cvLabel')} *
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">{t('microcopy.guestApply.justEmailAndCV')}</p>
                    <div 
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                        ${guestCvFile ? 'border-green-500 bg-green-50' : 'border-muted-foreground/25 hover:border-brand/50'}`}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="cv-upload-area"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.rtf"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="cv-file-input"
                      />
                      {guestCvFile ? (
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">{guestCvFile.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            {t('jobs.guestApply.dragDropCv')}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('jobs.guestApply.cvHelp')}
                          </p>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('jobs.guestApply.cvTip')}</p>
                  </div>
                  
                  {/* Optional Cover Letter */}
                  <div>
                    <Label>{t('jobs.guestApply.coverLetterOptional')}</Label>
                    <Textarea
                      placeholder={t('jobs.coverLetterPlaceholder')}
                      value={guestCoverLetter}
                      onChange={(e) => setGuestCoverLetter(e.target.value)}
                      rows={4}
                      data-testid="guest-cover-letter-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('jobs.guestApply.coverLetterTip')}</p>
                  </div>
                  
                  {/* Privacy & Trust Note */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                    <Shield className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span>{t('microcopy.guestApply.secureUpload')}</span>
                  </div>
                  
                  {/* Submit Button */}
                  <Button 
                    className="w-full" 
                    onClick={handleGuestApply} 
                    disabled={applying || !guestEmail || !guestCvFile}
                    data-testid="guest-apply-btn"
                  >
                    {applying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('microcopy.guestApply.almost')}
                      </>
                    ) : (
                      <>
                        {t('jobs.guestApply.submitButton')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    {t('microcopy.guestApply.freeForever')}
                  </p>
                  
                  {/* Login Link */}
                  <p className="text-center text-sm text-muted-foreground">
                    {t('jobs.guestApply.alreadyHaveAccount')}{' '}
                    <Link to="/login" className="text-brand hover:underline">
                      {t('jobs.guestApply.loginInstead')}
                    </Link>
                  </p>
                </div>
              </div>
            )}
            
            <Link to="/jobs" className="w-full">
              <Button variant="outline" className="w-full">{t('jobs.backToJobs')}</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
